// Code-based TanStack Router tree. Downstream tracks add their pages here
// (or migrate to file-based routes later — this is intentionally simple for
// Track 0).

import { createRootRoute, createRoute, redirect } from '@tanstack/react-router';
import { RootLayout } from '@/components/layout/root-layout';
import { LoginPage } from './login';
import { ChangePasswordPage } from './change-password';
import { ProfilePage } from './profile';
import { DashboardPage } from './dashboard';
import { LookupsPage } from './admin/lookups';
import { CustomersListPage } from './customers/list';
import { CustomerDetailPage } from './customers/detail';
import { TestersListPage } from './testers/list';
import { TesterDetailPage } from './testers/detail';
import { TesterFormPage } from './testers/form';
import { TesterCompensationPage } from './testers/compensation';
import { AccountsListPage } from './accounts/list';
import { AccountDetailPage } from './accounts/detail';
import { AccountFormPage } from './accounts/form';
import {
  DeviceDetailPage,
  DevicesListPage,
  PhoneDetailPage,
  PhoneNumbersListPage,
} from './assets/pages';
import { ProfileDetailPage, ProfilesListPage } from './profiles/pages';
import { AdminUsersPage } from './admin/users';
import { AdminAuditLogPage } from './admin/audit-log';
import { AdminConfigPage } from './admin/config';
import { AdminImportsPage } from './admin/imports';
import { NotificationsPage } from './notifications';
import { AccountSummaryPage } from './reports/account-summary';
import { MonthlyTrackingPage } from './reports/monthly-tracking';
import { AccountsHistoryPage } from './reports/accounts-history';
import { TransactionsExportPage } from './reports/transactions-export';
import { LinkedAccountsExportPage } from './reports/linked-accounts';
import { CustomBuilderPage } from './reports/custom-builder';
import { TemplatesPage } from './reports/templates';
import { TillerUploadPage } from '@/pages/tiller-sync/upload';
import { TillerMappingPage } from '@/pages/tiller-sync/mapping';
import { TillerUnmappedPage } from '@/pages/tiller-sync/unmapped';
import { TillerHistoryPage } from '@/pages/tiller-sync/history';
import { SnapshotPeriodsPage } from '@/pages/snapshots/periods';
import { SnapshotPreviewPage } from '@/pages/snapshots/preview';
import { SnapshotPeriodDetailPage } from '@/pages/snapshots/detail';
import { SnapshotDetailPage } from '@/pages/snapshots/snapshot';
import { PeriodGridPage } from '@/pages/manual-entry/period-grid';
import { auth } from '@/lib/api';

const rootRoute = createRootRoute({ component: RootLayout });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: async () => {
    try {
      const me = await auth.me();
      if (me.user.must_reset) throw redirect({ to: '/change-password' });
    } catch (err) {
      if (err && (err as { status?: number }).status === 401) {
        throw redirect({ to: '/login' });
      }
    }
  },
  component: DashboardPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});
const changePasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/change-password',
  component: ChangePasswordPage,
});
const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/profile',
  component: ProfilePage,
});

// Implemented in Track 0
const lookupsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/lookups',
  component: LookupsPage,
});

// Track 1 — Reference Data CRUD
const accountsListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accounts',
  // Search params: optional comma-separated `ids` used by dashboard
  // "Review" deep-links to scope the list to specific accounts.
  validateSearch: (s: Record<string, unknown>): { ids?: string } => ({
    ids: typeof s.ids === 'string' ? s.ids : undefined,
  }),
  component: AccountsListPage,
});
const accountNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accounts/new',
  component: () => <AccountFormPage mode="create" />,
});
const accountDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accounts/$id',
  component: AccountDetailPage,
});
const accountEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accounts/$id/edit',
  component: () => <AccountFormPage mode="edit" />,
});
const testersListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/testers',
  component: TestersListPage,
});
const testerCompensationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/testers/compensation',
  component: TesterCompensationPage,
});
const testerNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/testers/new',
  component: () => <TesterFormPage mode="create" />,
});
const testerDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/testers/$id',
  component: TesterDetailPage,
});
const testerEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/testers/$id/edit',
  component: () => <TesterFormPage mode="edit" />,
});
const profilesListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/profiles',
  component: ProfilesListPage,
});
const profileDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/profiles/$id',
  component: ProfileDetailPage,
});
const devicesListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/assets/devices',
  component: DevicesListPage,
});
const deviceDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/assets/devices/$id',
  component: DeviceDetailPage,
});
const phonesListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/assets/phones',
  component: PhoneNumbersListPage,
});
const phoneDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/assets/phones/$id',
  component: PhoneDetailPage,
});
const customersListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/customers',
  component: CustomersListPage,
});
const customerDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/customers/$id',
  component: CustomerDetailPage,
});
const tillerUploadRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tiller/upload',
  component: TillerUploadPage,
});
const tillerMappingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tiller/mapping',
  component: TillerMappingPage,
});
const tillerUnmappedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tiller/unmapped',
  component: TillerUnmappedPage,
});
const tillerHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tiller/history',
  component: TillerHistoryPage,
});
const manualEntryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/manual-entry',
  component: PeriodGridPage,
});
// Track 4 — Snapshots & Period close
const snapshotsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/snapshots',
  component: SnapshotPeriodsPage,
});
const snapshotPeriodDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/snapshots/$period',
  component: SnapshotPeriodDetailPage,
});
const snapshotPeriodPreviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/snapshots/$period/preview',
  component: SnapshotPreviewPage,
});
const snapshotDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/snapshots/snapshot/$id',
  component: SnapshotDetailPage,
});
const reportsAccountSummaryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/account-summary',
  component: AccountSummaryPage,
});
const reportsMonthlyTrackingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/monthly-tracking',
  component: MonthlyTrackingPage,
});
const reportsAccountsHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/accounts-history',
  component: AccountsHistoryPage,
});
const reportsTransactionsExportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/transactions-export',
  component: TransactionsExportPage,
});
const reportsLinkedAccountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/linked-accounts',
  component: LinkedAccountsExportPage,
});
const reportsBuilderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/builder',
  component: CustomBuilderPage,
});
const reportsTemplatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/templates',
  component: TemplatesPage,
});
const adminUsersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/users',
  component: AdminUsersPage,
});
const adminAuditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/audit',
  component: AdminAuditLogPage,
});
const adminConfigRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/config',
  component: AdminConfigPage,
});
const adminImportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/imports',
  component: AdminImportsPage,
});
const notificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/notifications',
  component: NotificationsPage,
});

export const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  changePasswordRoute,
  profileRoute,
  lookupsRoute,
  accountsListRoute,
  accountNewRoute,
  accountDetailRoute,
  accountEditRoute,
  testersListRoute,
  testerCompensationRoute,
  testerNewRoute,
  testerDetailRoute,
  testerEditRoute,
  profilesListRoute,
  profileDetailRoute,
  devicesListRoute,
  deviceDetailRoute,
  phonesListRoute,
  phoneDetailRoute,
  customersListRoute,
  customerDetailRoute,
  tillerUploadRoute,
  tillerMappingRoute,
  tillerUnmappedRoute,
  tillerHistoryRoute,
  manualEntryRoute,
  snapshotsRoute,
  snapshotPeriodDetailRoute,
  snapshotPeriodPreviewRoute,
  snapshotDetailRoute,
  reportsAccountSummaryRoute,
  reportsMonthlyTrackingRoute,
  reportsAccountsHistoryRoute,
  reportsTransactionsExportRoute,
  reportsLinkedAccountsRoute,
  reportsBuilderRoute,
  reportsTemplatesRoute,
  adminUsersRoute,
  adminAuditRoute,
  adminConfigRoute,
  adminImportsRoute,
  notificationsRoute,
]);
